import { API_ENDPOINT } from '../constants';
import { HTTP } from './http.service';

// ---------------------------------------------------------------------------
// Tipos alineados con raptor-cpd/src/modules/cpd/dto/cpd.schemas.ts
// ---------------------------------------------------------------------------
export type CpdMode = 'energy' | 'tone' | 'dtmf' | 'ml' | 'voice';
export type CpdAction = 'hangup_603' | 'continue' | 'transfer';

/** Umbrales de `CPD_DEFAULT` en el backend (`domain/cpd-config.ts`). */
export const CPD_DEFAULT_THRESHOLDS = {
  maxAnalysisMs: 8000,
  minEnergyDbfs: -40,
  minVoiceMs: 1500,
  maxSilenceMs: 1200
} as const;

/**
 * Claves que `webitel/mod_amd` parsea de verdad en los argumentos de la app
 * `amd`. Copiadas de `AMD_PARAM_KEYS` en el backend: el módulo ignora en
 * silencio las claves que no conoce, asi que un typo dejaria el AMD corriendo
 * con otros valores sin que nadie se enterara.
 */
export const AMD_PARAM_KEYS = [
  'initial_silence',
  'greeting',
  'after_greeting_silence',
  'total_analysis_time',
  'min_word_length',
  'between_words_silence',
  'maximum_number_of_words',
  'maximum_word_length',
  'silence_threshold',
  'silence_notsure'
] as const;

const VOICE_SPEC_PAIR_REGEX = /^[a-z_]+=\d{1,6}$/;

/**
 * Espejo de `validateVoiceSpec` del backend: `clave=entero` separado por comas,
 * con claves de `AMD_PARAM_KEYS`. Devuelve `null` si es valido o el mensaje de
 * error listo para el form.
 */
export function validateVoiceSpec(spec: string): string | null {
  if (spec.length > 255) return 'Maximo 255 caracteres';
  for (const part of spec.split(',')) {
    const p = part.trim();
    if (!VOICE_SPEC_PAIR_REGEX.test(p)) {
      return `"${p}" no tiene la forma clave=entero`;
    }
    const key = p.slice(0, p.indexOf('='));
    if (!(AMD_PARAM_KEYS as readonly string[]).includes(key)) {
      return `"${key}" no es una clave de mod_amd`;
    }
  }
  return null;
}

export interface CpdThresholds {
  maxAnalysisMs?: number;
  minEnergyDbfs?: number;
  minVoiceMs?: number;
  maxSilenceMs?: number;
  dtmfHumanDigits?: string;
  mlConfidenceMin?: number;
}

/** Override parcial: solo se envían las llaves que se quieren forzar (schema .strict()). */
export interface CpdOverrides {
  mode?: CpdMode;
  thresholds?: CpdThresholds;
  action?: CpdAction;
  transferTo?: string;
  toneSpec?: string;
  toneCarrier?: string;
  toneStartDelayMs?: number;
  voiceSpec?: string;
  bypass?: boolean;
}

export interface CpdOperatorUpsert {
  operatorId: string;
  description?: string;
  enabled: boolean;
  templateId: string | null;
  overrides?: CpdOverrides;
}

export interface CpdRouteUpsert {
  routeGroupId: number;
  description?: string;
  enabled: boolean;
  templateId: string | null;
  carrier?: string;
  overrides?: CpdOverrides;
}

export interface CpdRouteOperatorUpsert {
  operatorId: string;
  routeGroupId: number;
  description?: string;
  enabled: boolean;
  templateId: string | null;
  overrides?: CpdOverrides;
}

export type CpdResolutionLayer = 'default' | 'template' | 'route' | 'operator' | 'route_operator';

export interface EffectiveCpdConfig {
  config: Record<string, any>;
  resolution: {
    applied: CpdResolutionLayer[];
    templateId: string | null;
    operatorId: string | null;
    routeGroupId: number | null;
  };
}

export interface CpdReloadResult {
  ok: boolean;
  subscribers: number;
}

const BASE = `${API_ENDPOINT.CORE}cpd`;

export const CPDService = {
  // ----- Resolve / Reload ---------------------------------------------------
  /** Config efectiva para (operatorId?, routeGroupId?). Para preview antes de guardar. */
  resolve: (params: { operatorId?: string; routeGroupId?: number | string }) =>
    HTTP.get<EffectiveCpdConfig, EffectiveCpdConfig>(`${BASE}/resolve`, { params }),
  /** Publica en Redis `cpd:config:changed` para que los FS recarguen su cache. */
  reload: () => HTTP.post<CpdReloadResult, CpdReloadResult>(`${BASE}/reload`, {}),

  // ----- Templates ----------------------------------------------------------
  saveTemplates: (data: any) => HTTP.post<any, any>(`${BASE}/templates`, data),
  updateTemplates: (id: string, data: any) => HTTP.put<any, any>(`${BASE}/templates/${id}`, data),
  getTemplates: () => HTTP.get<any, any>(`${BASE}/templates`),
  getTemplateById: (id: string) => HTTP.get<any, any>(`${BASE}/templates/${id}`),
  deleteTemplates: (id: string) => HTTP.delete<any, any>(`${BASE}/templates/${id}`),

  // ----- Operators ----------------------------------------------------------
  getOperators: () => HTTP.get<any, any>(`${BASE}/operators`),
  getOperatorById: (id: string) => HTTP.get<any, any>(`${BASE}/operators/${id}`),
  saveOperator: (data: CpdOperatorUpsert) => HTTP.post<any, any>(`${BASE}/operators`, data),
  updateOperator: (id: string, data: CpdOperatorUpsert) =>
    HTTP.put<any, any>(`${BASE}/operators/${id}`, data),
  deleteOperators: (id: string) => HTTP.delete<any, any>(`${BASE}/operators/${id}`),

  // ----- Routes -------------------------------------------------------------
  getRoutes: () => HTTP.get<any, any>(`${BASE}/routes`),
  getRouteById: (id: string) => HTTP.get<any, any>(`${BASE}/routes/${id}`),
  saveRoute: (data: CpdRouteUpsert) => HTTP.post<any, any>(`${BASE}/routes`, data),
  updateRoute: (id: string, data: CpdRouteUpsert) =>
    HTTP.put<any, any>(`${BASE}/routes/${id}`, data),
  deleteRoutes: (id: string) => HTTP.delete<any, any>(`${BASE}/routes/${id}`),

  // ----- Route-operators (overrides) ----------------------------------------
  getOverrides: () => HTTP.get<any, any>(`${BASE}/route-operators`),
  getOverrideById: (id: string) => HTTP.get<any, any>(`${BASE}/route-operators/${id}`),
  saveOverride: (data: CpdRouteOperatorUpsert) =>
    HTTP.post<any, any>(`${BASE}/route-operators`, data),
  updateOverride: (id: string, data: CpdRouteOperatorUpsert) =>
    HTTP.put<any, any>(`${BASE}/route-operators/${id}`, data),
  deleteOverrides: (id: string) => HTTP.delete<any, any>(`${BASE}/route-operators/${id}`)
};
